'use client';

import { faroService } from './FaroService';
import { observabilityService } from './ObservabilityService';
import { ga4Service } from './GA4Service';

interface PageViewData {
  path: string;
  title: string;
  timestamp: number;
  referrer?: string;
  userAgent?: string;
}

interface UserJourneyEvent {
  eventType: 'page_view' | 'feature_interaction' | 'conversion' | 'error_recovery';
  eventName: string;
  path: string;
  timestamp: number;
  sessionId: string;
  metadata?: Record<string, any>;
}

interface GameLifecycleEvent {
  gameType: string;
  action: 'room_create' | 'room_join' | 'room_leave' | 'game_start' | 'game_pause' | 'game_end';
  roomId?: string;
  participantCount?: number;
  duration?: number;
  metadata?: Record<string, any>;
}

interface ConversionFunnelStep {
  step: string;
  funnel: 'onboarding' | 'room_creation' | 'game_participation';
  success: boolean;
  duration?: number;
  errorReason?: string;
}

class RUMService {
  private sessionId: string;
  private pageViews: PageViewData[] = [];
  private userJourney: UserJourneyEvent[] = [];
  private sessionStartTime: number;
  private currentPath: string = '';
  private isInitialized: boolean = false;

  constructor() {
    this.sessionId = this._generateSessionId();
    this.sessionStartTime = Date.now();
  }

  /**
   * Initialize RUM service with Next.js router integration
   */
  initialize(): void {
    if (this.isInitialized) return;

    console.log('[RUMService] initialize() Starting RUM service initialization');

    // Track initial page load
    this._trackInitialPageLoad();

    // Set up Next.js navigation tracking
    this._setupNextJSNavigationTracking();

    // Set up Core Web Vitals tracking (handled by Faro automatically)
    this._setupWebVitalsTracking();

    // Set up error tracking
    this._setupErrorTracking();

    // Set up user engagement tracking
    this._setupEngagementTracking();

    this.isInitialized = true;
    console.log('[RUMService] initialize() RUM service initialized successfully');
  }

  /**
   * Track page view manually (for SPA navigation)
   */
  trackPageView(path: string, title?: string): void {

    
    const pageView: PageViewData = {
      path,
      title: title || document.title,
      timestamp: Date.now(),
      referrer: document.referrer,
      userAgent: navigator.userAgent,
    };

    this.pageViews.push(pageView);
    this.currentPath = path;

    // Send to Faro, custom pipeline, and GA4
    this._sendPageViewToFaro(pageView);
    this._sendPageViewToCustomPipeline(pageView);
    ga4Service.trackPageView(pageView.path, pageView.title);

    // Track in user journey
    this._addToUserJourney({
      eventType: 'page_view',
      eventName: 'page_navigation',
      path,
      timestamp: Date.now(),
      sessionId: this.sessionId,
      metadata: { title: pageView.title }
    });


  }

  /**
   * Track game lifecycle events
   */
  trackGameLifecycle(event: GameLifecycleEvent): void {
    const timestamp = Date.now();

    // Send to Faro as custom event
    faroService.trackEvent('game_lifecycle', {
      gameType: event.gameType,
      action: event.action,
      roomId: event.roomId ? 'present' : 'none', // Privacy conscious
      participantCount: event.participantCount,
      duration: event.duration,
      sessionId: this.sessionId,
      ...event.metadata
    });

    // Mirror to GA4
    ga4Service.trackGameLifecycle(event);

    // Send to custom pipeline for business analytics
    observabilityService.trackBusinessEvent('game_lifecycle', {
      gameType: event.gameType,
      action: event.action,
      roomId: event.roomId,
      participantCount: event.participantCount,
      duration: event.duration,
      timestamp,
      sessionId: this.sessionId,
      ...event.metadata
    });

    // Track in user journey
    this._addToUserJourney({
      eventType: 'feature_interaction',
      eventName: `game_${event.action}`,
      path: this.currentPath,
      timestamp,
      sessionId: this.sessionId,
      metadata: event
    });

    console.log('[page-metrics] [RUMService] trackGameLifecycle() Tracked game event:', event.action, event.gameType);
  }

  /**
   * Track conversion funnel steps
   */
  trackConversionStep(step: ConversionFunnelStep): void {
    const timestamp = Date.now();

    // Send to Faro as custom event
    faroService.trackEvent('conversion_funnel', {
      step: step.step,
      funnel: step.funnel,
      success: step.success,
      duration: step.duration,
      errorReason: step.errorReason,
      sessionId: this.sessionId,
    });

    // Mirror to GA4
    ga4Service.trackConversionStep(step);

    // Send to custom pipeline
    observabilityService.trackBusinessEvent('conversion_funnel', {
      step: step.step,
      funnel: step.funnel,
      success: step.success,
      duration: step.duration,
      errorReason: step.errorReason,
      timestamp,
      sessionId: this.sessionId,
      path: this.currentPath
    });

    // Track in user journey
    this._addToUserJourney({
      eventType: 'conversion',
      eventName: `${step.funnel}_${step.step}`,
      path: this.currentPath,
      timestamp,
      sessionId: this.sessionId,
      metadata: step
    });

    console.log('[page-metrics] [RUMService] trackConversionStep() Tracked conversion:', step.funnel, step.step, step.success);
  }

  /**
   * Track feature adoption and usage patterns
   */
  trackFeatureUsage(featureName: string, action: string, metadata?: Record<string, any>): void {
    const timestamp = Date.now();

    // Send to Faro as custom event
    faroService.trackEvent('feature_usage', {
      feature: featureName,
      action,
      sessionId: this.sessionId,
      path: this.currentPath,
      ...metadata
    });

    // Mirror to GA4
    ga4Service.trackFeatureUsage(featureName, action, metadata);

    // Send to custom pipeline
    observabilityService.trackBusinessEvent('feature_usage', {
      feature: featureName,
      action,
      timestamp,
      sessionId: this.sessionId,
      path: this.currentPath,
      ...metadata
    });

    // Track in user journey
    this._addToUserJourney({
      eventType: 'feature_interaction',
      eventName: `${featureName}_${action}`,
      path: this.currentPath,
      timestamp,
      sessionId: this.sessionId,
      metadata: { feature: featureName, action, ...metadata }
    });

    console.log('[page-metrics] [RUMService] trackFeatureUsage() Tracked feature usage:', featureName, action);
  }

  /**
   * Track error recovery success
   */
  trackErrorRecovery(errorType: string, recoveryAction: string, success: boolean, metadata?: Record<string, any>): void {
    const timestamp = Date.now();

    // Send to Faro as custom event
    faroService.trackEvent('error_recovery', {
      errorType,
      recoveryAction,
      success,
      sessionId: this.sessionId,
      path: this.currentPath,
      ...metadata
    });

    // Mirror to GA4
    ga4Service.trackErrorRecovery(errorType, recoveryAction, success);

    // Send to custom pipeline
    observabilityService.trackBusinessEvent('error_recovery', {
      errorType,
      recoveryAction,
      success,
      timestamp,
      sessionId: this.sessionId,
      path: this.currentPath,
      ...metadata
    });

    // Track in user journey
    this._addToUserJourney({
      eventType: 'error_recovery',
      eventName: `${errorType}_recovery`,
      path: this.currentPath,
      timestamp,
      sessionId: this.sessionId,
      metadata: { errorType, recoveryAction, success, ...metadata }
    });

    console.log('[page-metrics] [RUMService] trackErrorRecovery() Tracked error recovery:', errorType, success);
  }

  /**
   * Get session analytics summary
   */
  getSessionSummary(): {
    sessionId: string;
    duration: number;
    pageViews: number;
    userJourneyEvents: number;
    currentPath: string;
  } {
    return {
      sessionId: this.sessionId,
      duration: Date.now() - this.sessionStartTime,
      pageViews: this.pageViews.length,
      userJourneyEvents: this.userJourney.length,
      currentPath: this.currentPath
    };
  }

  // Private helper methods

  private _generateSessionId(): string {
    return `rum_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private _trackInitialPageLoad(): void {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      const title = document.title;
      this.trackPageView(path, title);
    }
  }

  private _setupNextJSNavigationTracking(): void {
    if (typeof window !== 'undefined') {
      // Track browser navigation (back/forward buttons)
      window.addEventListener('popstate', () => {
        this.trackPageView(window.location.pathname);
      });

      // Track programmatic navigation (Next.js router)
      // This will be enhanced when we integrate with Next.js router hooks
      const originalPushState = history.pushState;
      const originalReplaceState = history.replaceState;

      history.pushState = function(...args) {
        originalPushState.apply(history, args);
        setTimeout(() => {
          rumService.trackPageView(window.location.pathname);
        }, 0);
      };

      history.replaceState = function(...args) {
        originalReplaceState.apply(history, args);
        setTimeout(() => {
          rumService.trackPageView(window.location.pathname);
        }, 0);
      };
    }
  }

  private _setupWebVitalsTracking(): void {
    // Core Web Vitals are automatically tracked by Faro SDK
    // We can add custom measurements here if needed
    // console.log('[page-metrics] [RUMService] _setupWebVitalsTracking() Core Web Vitals tracking enabled via Faro SDK');
  }

  private _setupErrorTracking(): void {
    if (typeof window !== 'undefined') {
      // Faro SDK handles automatic error tracking
      // Add custom error recovery tracking
      window.addEventListener('error', (event) => {
        console.log('[page-metrics] [RUMService] _setupErrorTracking() JavaScript error detected:', event.error?.message);
      });

      window.addEventListener('unhandledrejection', (event) => {
        console.log('[page-metrics] [RUMService] _setupErrorTracking() Unhandled promise rejection detected:', event.reason);
      });
    }
  }

  private _setupEngagementTracking(): void {
    if (typeof window !== 'undefined') {
      // Track user engagement metrics
      let engagementStartTime = Date.now();
      let isEngaged = true;

      // Track page visibility changes
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          if (isEngaged) {
            const engagementDuration = Date.now() - engagementStartTime;
            this.trackFeatureUsage('page_engagement', 'session_pause', {
              duration: engagementDuration,
              path: this.currentPath
            });
            isEngaged = false;
          }
        } else {
          engagementStartTime = Date.now();
          isEngaged = true;
          this.trackFeatureUsage('page_engagement', 'session_resume', {
            path: this.currentPath
          });
        }
      });

      // Track user activity (clicks, scrolls, etc.)
      let lastActivityTime = Date.now();
      const trackActivity = () => {
        lastActivityTime = Date.now();
      };

      ['click', 'scroll', 'keydown', 'mousemove', 'touchstart'].forEach(eventType => {
        document.addEventListener(eventType, trackActivity, { passive: true });
      });

      // Check for user inactivity every 30 seconds
      setInterval(() => {
        const inactiveTime = Date.now() - lastActivityTime;
        if (inactiveTime > 300000 && isEngaged) { // 5 minutes of inactivity
          this.trackFeatureUsage('page_engagement', 'user_inactive', {
            inactiveTime,
            path: this.currentPath
          });
        }
      }, 30000);
    }
  }

  private _sendPageViewToFaro(pageView: PageViewData): void {
    if (faroService.isReady()) {
      faroService.trackEvent('page_view', {
        path: pageView.path,
        title: pageView.title,
        sessionId: this.sessionId,
        timestamp: pageView.timestamp
      });
    }
  }

  private _sendPageViewToCustomPipeline(pageView: PageViewData): void {
    observabilityService.trackBusinessEvent('page_view', {
      path: pageView.path,
      title: pageView.title,
      sessionId: this.sessionId,
      timestamp: pageView.timestamp,
      referrer: pageView.referrer,
      userAgent: pageView.userAgent
    });
  }

  private _addToUserJourney(event: UserJourneyEvent): void {
    this.userJourney.push(event);

    // Keep only last 50 events to prevent memory issues
    if (this.userJourney.length > 50) {
      this.userJourney = this.userJourney.slice(-50);
    }
  }
}

// Singleton instance
export const rumService = new RUMService();

// Export types for use in other components
export type { GameLifecycleEvent, ConversionFunnelStep, UserJourneyEvent }; 